import type {
    SectionTree,
    SectionTreeItem,
} from '../../../ecommerce/components/SectionMenu/SectionMenu.types';

import { isDevelopmentEnvironment } from '../../../../utility/environments';
import { urlFriendlySlug } from '../../utils/sanitizeUtils';
import getDirectHybrisClient from '../clients/directHybrisAxiosClient';

/**
 * Fetch the section tree based on user and store.
 * @param token token if user is authenticated
 * @param productionUnit productionunit to fetch tree from
 * @param userId the id of the user, for anoymous users, valid values are 'anonymous' or 'anonymousb2b'
 * @returns the section tree for the given production unit and user
 */
const getSectionTree = async (
    token: string | undefined,
    productionUnit: string,
    userId: string | 'anonymous' | 'anonymousb2b',
) => {
    const response = await getDirectHybrisClient(token).get<NavigationCategoryNodeListWsDTO>(
        `/users/${encodeURIComponent(userId)}/categories/tree/${encodeURIComponent(
            productionUnit,
        )}`,
    );
    const start = Date.now();
    const mapped = mapToSectionTree(response?.data);
    const end = Date.now();
    if (isDevelopmentEnvironment) {
        // eslint-disable-next-line no-console
        console.log(`Mapping of categories took: ${end - start} milliseconds`);
    }
    return mapped;
};

export const directSectionsApi = {
    getSectionTree,
};

const mapToSectionTree = (tree: NavigationCategoryNodeListWsDTO): SectionTree => {
    if (!tree || !tree.nodes) return [];

    // for some reason, hybris has categories with empty names
    const mapToApiSectionTreeList = (nodes: NavigationCategoryNodeWsDTO[], level: number) =>
        nodes
            .map((node) => mapToApiSectionTree(node, level))
            .filter((n): n is SectionTreeItem => n !== undefined);

    const mapToApiSectionTree = (
        node: NavigationCategoryNodeWsDTO,
        lvl: number,
    ): SectionTreeItem | undefined =>
        node.name
            ? {
                  id: node.code.toString(),
                  name: node.name,
                  escapedName: urlFriendlySlug(node.name)!,
                  level: lvl,
                  pageTitleSupplement: node.pageTitleSupplement || '',
                  metaDescription: node.metaDescription || '',
                  canonicalPath: node.canonicalPath,
                  heading: node.heading || '',
                  description: node.description || '',
                  children: node.children ? mapToApiSectionTreeList(node.children, lvl + 1) : [],
              }
            : undefined;

    return mapToApiSectionTreeList(tree.nodes, 1);
};

interface NavigationCategoryNodeWsDTO {
    children?: NavigationCategoryNodeWsDTO[];
    code: number;
    name: string;
    url: string;
    pageTitleSupplement?: string;
    metaDescription?: string;
    canonicalPath: string;
    heading?: string;
    description?: string;
}

interface NavigationCategoryNodeListWsDTO {
    nodes: NavigationCategoryNodeWsDTO[];
}
